import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { ArrowsClockwise } from "@phosphor-icons/react";

import AssessmentBanner from "./AssessmentBanner";
import AssessmentChart from "./AssessmentChart";
import AverageScore from "./AverageScore";
import { getAssessmentDefinition } from "../assessmentConfig";
import { assessmentCard, assessmentCardData } from "../styled";
import type { AssessmentRecord, AssessmentType } from "../types";
import type { AssessmentCardState } from "../hooks/useRawAssessments";
import noAssessment from "assets/no_assessment.png";
import assessmentError from "assets/assessment_error.png";

interface AssessmentCardProps {
  assessmentType: AssessmentType;
  dataAssessments: AssessmentRecord[];
  cardState?: AssessmentCardState;
  onRefresh?: () => void;
  onOpenResults: (assessmentType: AssessmentType) => void;
  onOpenAddAssessment: (assessmentType: AssessmentType) => void;
  onOpenSendQabfDialog?: () => void;
  /** Called when the clinician clicks "Send Assessment" on CFQL-2 to create a client task */
  onSendCfql2Assessment?: () => void;
  /** Disables the CFQL-2 "Send Assessment" button (e.g. while a CFQL task is already pending) */
  sendCfqlDisabled?: boolean;
  /** Overrides the FillOut URL from the assessment definition (e.g., to include a client guid) */
  filloutUrlOverride?: string;
}

const ABBREVIATED_ASSESSMENT_NAMES: Record<AssessmentType, string> = {
  vineland: "Vineland",
  cfql2: "CFQL-2",
  vbmappMilestones: "VB-MAPP Milestones",
  afls: "AFLS",
  vbmappBarriers: "VB-MAPP Barriers",
  vbmappTransition: "VB-MAPP Transition",
  sociallySavvy: "Socially Savvy",
  srs2: "SRS-2",
  peak: "PEAK",
  efl: "EFL",
  qabf: "QABF",
  cpfq: "CPFQ",
};

// CTA labels are provided by `assessmentConfig` per-assessment; do not override here.

interface StateContentProps {
  state: AssessmentCardState;
  assessmentName: string;
  onRefresh?: () => void;
}

function AssessmentCardStateContent({ state, assessmentName, onRefresh }: StateContentProps) {
  const isError = state === "error";

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: 520,
        px: 6,
        py: 8,
        textAlign: "center",
      }}
      rowGap={3}
    >
      <Box
        component="img"
        src={isError ? assessmentError : noAssessment}
        alt={isError ? "Could not load results" : "No results yet"}
        sx={{ width: 260, maxWidth: "100%", opacity: 0.75 }}
      />

      {isError ? (
        <>
          <Typography variant="h2">Couldn&apos;t load results.</Typography>
          <Typography variant="body1" color="text.secondary">
            Refresh to try again.
          </Typography>

          <Button
            color="secondary"
            variant="outlined"
            onClick={onRefresh}
            startIcon={<ArrowsClockwise size={20} />}
            sx={{ mt: 2 }}
          >
            Refresh
          </Button>
        </>
      ) : (
        <>
          <Typography variant="h2">No results yet.</Typography>
          <Typography variant="body1" color="text.secondary">
            {`Results from completed ${assessmentName} assessments will appear here.`}
          </Typography>
        </>
      )}
    </Stack>
  );
}

export default function AssessmentCard({
  assessmentType,
  dataAssessments,
  cardState = "ready",
  onRefresh,
  onOpenResults,
  onOpenAddAssessment,
  onOpenSendQabfDialog,
  onSendCfql2Assessment,
  sendCfqlDisabled,
  filloutUrlOverride,
}: AssessmentCardProps) {
  const definition = getAssessmentDefinition(assessmentType);
  const abbreviatedName = ABBREVIATED_ASSESSMENT_NAMES[assessmentType];
  const isStateVariant = cardState === "noData" || cardState === "error";
  // Secondary action buttons (Add/Send/Start) always show so clinicians can add data when none exists
  const hideSecondaryAction = definition.secondaryAction === undefined;

  const secondaryActionClick =
    assessmentType === "qabf"
      ? onOpenSendQabfDialog
      : assessmentType === "cfql2"
        ? onSendCfql2Assessment
        : undefined;

  return (
    <Box sx={assessmentCard} data-testid={`assessment-card-${assessmentType}`}>
      <AssessmentBanner
        definition={definition}
        onViewScores={() => onOpenResults(assessmentType)}
        onAddAssessment={() => onOpenAddAssessment(assessmentType)}
        hideActions={false}
        hidePrimaryAction={isStateVariant}
        hideSecondaryAction={hideSecondaryAction}
        surface="card"
        onSecondaryActionClick={secondaryActionClick}
        secondaryActionDisabled={assessmentType === "cfql2" ? sendCfqlDisabled : undefined}
        filloutUrlOverride={filloutUrlOverride}
      />

      <Box sx={assessmentCardData}>
        {isStateVariant ? (
          <AssessmentCardStateContent
            state={cardState}
            assessmentName={abbreviatedName}
            onRefresh={onRefresh}
          />
        ) : (
          <>
            {assessmentType === "vineland" && dataAssessments.length > 0 && (
              <AverageScore dataAssessments={dataAssessments} />
            )}

            <AssessmentChart assessmentType={assessmentType} dataAssessments={dataAssessments} />
          </>
        )}
      </Box>
    </Box>
  );
}
